/**
 * Menú móvil (estándar §2.2 y §2.4).
 *
 * Está siempre montado y se abre y cierra con clases: cerrar es un cambio inmediato y la
 * animación es solo decoración. Superpuesto es un diálogo: el foco queda atrapado dentro, el
 * resto de la página pasa a `inert` y, al cerrar, el foco vuelve al botón que lo abrió. Se
 * cierra con su botón, tocando el velo, con Escape, al pulsar cualquier enlace y al pasar a
 * escritorio.
 *
 * Escudo de doble clic: tras cerrarse, el velo sigue recogiendo clics 400 ms, ya invisible.
 * Sin él, el segundo clic de un doble clic sobre el velo atravesaba el menú recién cerrado y
 * pulsaba lo que hubiera debajo.
 */
export function iniciarMenu(): void {
  const abrir = document.querySelector<HTMLButtonElement>("[data-menu-abrir]");
  const panel = document.querySelector<HTMLElement>("[data-menu-panel]");
  const velo = document.querySelector<HTMLElement>("[data-menu-velo]");
  const cerrar = panel?.querySelector<HTMLButtonElement>("[data-menu-cerrar]");
  if (!abrir || !panel || !velo || !cerrar) return;

  const fondo = ["header.cabecera", "main", "footer"]
    .map((s) => document.querySelector<HTMLElement>(s))
    .filter((el): el is HTMLElement => el !== null);
  let abierto = false;
  let escudo = 0;

  const enfocables = () =>
    Array.from(panel.querySelectorAll<HTMLElement>("a[href], button:not([disabled])")).filter(
      (el) => el.offsetParent !== null,
    );

  function abrirMenu() {
    if (abierto) return;
    abierto = true;
    window.clearTimeout(escudo);
    velo!.classList.remove("escudo");
    panel!.classList.add("abierto");
    velo!.classList.add("abierto");
    abrir!.setAttribute("aria-expanded", "true");
    fondo.forEach((el) => el.setAttribute("inert", ""));
    document.documentElement.style.overflow = "hidden";
    // El panel ya es visible en este fotograma (la clase quita `visibility: hidden`).
    window.requestAnimationFrame(() => cerrar!.focus());
  }

  function cerrarMenu(devolverFoco = true) {
    if (!abierto) return;
    abierto = false;
    panel!.classList.remove("abierto");
    velo!.classList.remove("abierto");
    velo!.classList.add("escudo");
    escudo = window.setTimeout(() => velo!.classList.remove("escudo"), 400);
    abrir!.setAttribute("aria-expanded", "false");
    fondo.forEach((el) => el.removeAttribute("inert"));
    document.documentElement.style.overflow = "";
    if (devolverFoco) abrir!.focus();
  }

  abrir.addEventListener("click", abrirMenu);
  cerrar.addEventListener("click", () => cerrarMenu());
  velo.addEventListener("click", () => cerrarMenu());
  // Al pulsar un enlace se cierra sin devolver el foco al botón: la persona va a otra parte.
  panel.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("a")) cerrarMenu(false);
  });

  document.addEventListener("keydown", (e) => {
    if (!abierto) return;
    if (e.key === "Escape") {
      e.preventDefault();
      cerrarMenu();
      return;
    }
    if (e.key !== "Tab") return;
    const lista = enfocables();
    if (!lista.length) return;
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  });

  window.matchMedia("(min-width: 1024px)").addEventListener("change", (m) => {
    if (m.matches) cerrarMenu(false);
  });
}
